import { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles, Bot, User } from 'lucide-react';
import useAutomationsStore from '../../../../../store/useAutomationsStore';

export default function AIChatPanel({ api, companyId, workflowId, onClose }) {
  const { nodes, edges, setNodes, setEdges } = useAutomationsStore();
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Describe what you want to change in your workflow.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: prompt }]);
    setLoading(true);

    try {
      const data = await api.modifyWorkflow(companyId, workflowId, prompt, { nodes, edges });
      const raw = data.content || data.generation || data;
      const generation = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const newNodes = generation.nodes || [];
      const newEdges = generation.edges || [];

      if (newNodes.length > 0) {
        setNodes(newNodes);
        setEdges(newEdges);
        setMessages((prev) => [...prev, { role: 'assistant', text: 'Workflow updated.' }]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', text: 'Could not apply changes. Try being more specific.' }]);
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="w-[340px] bg-white border-l border-gray-200 h-full flex flex-col shrink-0 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-gray-800">AI Assistant</h3>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition">
          <X className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-violet-600" />
              </div>
            )}
            <div
              className={[
                'max-w-[85%] px-3 py-2 rounded-xl text-sm',
                msg.role === 'user'
                  ? 'bg-gray-900 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-700 rounded-bl-sm',
              ].join(' ')}
            >
              {msg.text}
            </div>
            {msg.role === 'user' && (
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-gray-500" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-violet-600" />
            </div>
            <div className="px-3 py-2 rounded-xl bg-gray-100 rounded-bl-sm">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Add a Slack notification after the email..."
            rows={2}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-none"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="self-end shrink-0 p-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
